import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useCallback, useContext, useRef } from 'react';
import RcbOrderState from '~/constants/common/OrderState';
import { useIsLoggedIn } from '~/context/common/UserContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { fetchUserPurchasing as fetchRcbUserPurchasing } from '~/services/common';
import UserPurchaseData from '~/services/common/contracts/UserPurchaseData';
import { generateErrorLogger } from '~/utilities/loggers';

export function useGetRcbUserPurchasingGetter() {
  const {
    rcbPurchasing: { setData, setError, setLoading, setLoaded }
  } = useContext(UserVehicleContext);
  const isLoggedIn = useIsLoggedIn();
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;

  return useCallback<
    (controller?: AbortController) => Promise<UserPurchaseData>
  >(
    async controller => {
      if (!isLoggedInRef.current) {
        setLoaded();

        return {
          orderState: RcbOrderState.None
        };
      }

      const errorLogger = generateErrorLogger(
        'Get rcb user purchasing',
        {},
        { callback: setError, controller }
      );

      try {
        setLoading();

        const { orderState, ...details } =
          await fetchRcbUserPurchasing(controller);

        const purchasing = {
          orderState,
          ...details
        };

        setData(purchasing);

        return purchasing;
      } catch (err) {
        errorLogger(err);

        return {
          orderState: RcbOrderState.None
        };
      }
    },
    // isLoggedInRef is read at call time; isLoggedIn stays a dep so callers that key off this
    // function's identity (e.g. useGetUserPurchasing's effect) still refetch when login state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn, setData, setError, setLoaded, setLoading]
  );
}

export default function useGetUserPurchasing() {
  const { fetchVehicleAvailabilityFlag } = useContext(UserVehicleContext);
  const getUserPurchasing = useGetRcbUserPurchasingGetter();

  useEffectWithController(
    controller => {
      void getUserPurchasing(controller);
    },
    [fetchVehicleAvailabilityFlag, getUserPurchasing]
  );
}
